import { useEffect, useRef, useState } from 'react';
import { checklistHistoricalReadService } from '@/services/checklist/ChecklistHistoricalReadRuntime';
import { getChecklistHistoricalReadDependencies, type ChecklistHistoricalReadResult } from '@/services/checklist/ChecklistHistoricalReadService';
import type { Checklist, ChecklistField } from './useChecklistEditor';
import { UseChecklistEditorLogger } from '@/utils/logger';

type HistoricalRead = ChecklistHistoricalReadResult & { valuesReadVersion: number; scope: string };

export function useChecklistHistoricalRead(input: {
  checklist: Checklist | null;
  currentFields: ChecklistField[];
  currentValues: Record<string, string> | null;
  templateRetired: boolean;
  valuesReadVersion: number;
  identity: string;
  paused?: boolean;
}) {
  const { checklist, currentFields, currentValues, templateRetired, valuesReadVersion, identity, paused = false } = input;
  const scope = JSON.stringify([identity, ...getChecklistHistoricalReadDependencies(checklist), templateRetired]);
  const [read, setRead] = useState<HistoricalRead | null>(null);
  const [failure, setFailure] = useState<{ scope: string; message: string } | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const currentRead = useRef(read);
  currentRead.current = read;

  useEffect(() => {
    let active = true;
    if (!checklist || paused) return () => { active = false; };
    void checklistHistoricalReadService.resolve({ checklist, currentFields, currentValues, templateRetired, lifecycle: 'existing' })
      .then(result => {
        if (active && !pausedRef.current) {
          setRead({ ...result, valuesReadVersion, scope });
          setFailure(null);
        }
      }).catch(reason => {
        if (!active || pausedRef.current) return;
        // Uma leitura temporariamente indisponível não desmonta uma visão válida da mesma identidade.
        UseChecklistEditorLogger.Main.info(`[historicalRefresh] ${reason?.message || 'Falha na leitura histórica'}`);
        if (currentRead.current?.scope !== scope || reason?.name === 'ChecklistSnapshotIncompleteError') {
          setFailure({ scope, message: reason?.message || 'Erro ao ler o histórico do checklist.' });
        }
      });
    return () => { active = false; };
  }, [scope, currentFields, currentValues, templateRetired, valuesReadVersion, paused]);

  const matchingRead = read?.scope === scope ? read : null;
  return { historicalRead: matchingRead, historicalReadError: failure?.scope === scope ? failure.message : null,
    historicalReadReady: !checklist || !!matchingRead };
}
